import { Armchair, CheckCheck, Phone, UserX, Users } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { to12h } from '@/services/appointments.service';
import type { DayBookingFilter, DayBookingRow } from '@/services/tables.service';

const FILTERS: Array<{ value: 'all' | DayBookingFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'seated', label: 'Seated' },
  { value: 'finished', label: 'Finished' },
  { value: 'no_show', label: 'No-show' },
  { value: 'unassigned', label: 'Unassigned' },
];

interface Props {
  rows: DayBookingRow[];
  loading: boolean;
  filter: 'all' | DayBookingFilter;
  onFilterChange: (f: 'all' | DayBookingFilter) => void;
  onAssign: (row: DayBookingRow) => void;
  onSeat: (row: DayBookingRow) => void;
  onFinish: (row: DayBookingRow) => void;
  onNoShow: (row: DayBookingRow) => void;
}

export function BookingsPanel({ rows, loading, filter, onFilterChange, onAssign, onSeat, onFinish, onNoShow }: Props) {
  return (
    <div className="flex h-full flex-col rounded-2xl border border-border bg-surface shadow-soft">
      <div className="flex flex-wrap gap-1.5 border-b border-border p-3">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => onFilterChange(f.value)}
            className={clsx(
              'rounded-full px-3 py-1 text-2xs font-semibold transition-colors',
              filter === f.value ? 'bg-primary text-on-primary' : 'bg-surface-alt text-text-secondary hover:bg-primary-soft hover:text-primary',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto p-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)
        ) : rows.length === 0 ? (
          <EmptyState icon={<Armchair size={26} />} title="No bookings here" description="Nothing matches this filter for the selected date." />
        ) : (
          rows.map((r) => <BookingRow key={r.bookingUid} row={r} onAssign={onAssign} onSeat={onSeat} onFinish={onFinish} onNoShow={onNoShow} />)
        )}
      </div>
    </div>
  );
}

function BookingRow({ row, onAssign, onSeat, onFinish, onNoShow }: {
  row: DayBookingRow;
  onAssign: (r: DayBookingRow) => void;
  onSeat: (r: DayBookingRow) => void;
  onFinish: (r: DayBookingRow) => void;
  onNoShow: (r: DayBookingRow) => void;
}) {
  const seated = row.floorStatus === 'seated';
  const finished = row.floorStatus === 'finished';
  const noShow = row.floorStatus === 'no_show';

  return (
    <div className="rounded-xl border border-border bg-surface-alt/40 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-text-primary">{row.customerName ?? 'Guest'}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-2xs text-text-muted">
            <span>{to12h(row.slotStart)}</span>
            {row.partySize != null && <span className="inline-flex items-center gap-0.5"><Users size={11} /> {row.partySize}</span>}
            {row.customerPhone && <span className="inline-flex items-center gap-0.5"><Phone size={11} /> {row.customerPhone}</span>}
          </div>
        </div>
        <StatusBadge row={row} />
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="truncate text-2xs text-text-muted">
          {row.tableName ? `Table ${row.tableName}` : 'Unassigned'}
        </span>
        {!finished && !noShow && (
          <div className="flex shrink-0 gap-1.5">
            <Button size="sm" variant="ghost" onClick={() => onAssign(row)}>{row.tableUid ? 'Reassign' : 'Assign'}</Button>
            {!seated && (
              <Button size="sm" variant="ghost" iconLeft={<UserX size={13} />} onClick={() => onNoShow(row)}>No-show</Button>
            )}
            {!seated && row.tableUid && (
              <Button size="sm" variant="soft" onClick={() => onSeat(row)}>Seat</Button>
            )}
            {seated && (
              <Button size="sm" variant="soft" iconLeft={<CheckCheck size={13} />} onClick={() => onFinish(row)}>Finish</Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ row }: { row: DayBookingRow }) {
  if (row.floorStatus === 'seated') return <Badge className="bg-primary-soft text-primary">Seated</Badge>;
  if (row.floorStatus === 'finished') return <Badge className="bg-success-soft text-success">Finished</Badge>;
  if (row.floorStatus === 'no_show') return <Badge className="bg-surface-alt text-text-muted">No-show</Badge>;
  return <Badge className="bg-warning-soft text-warning">Waiting</Badge>;
}

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return <span className={clsx('shrink-0 rounded-full px-2 py-0.5 text-2xs font-semibold', className)}>{children}</span>;
}
